import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { fetchDoctorAppointments, fetchPatientAppointments, cancelAppointment } from "../../services/api";
import "./MeetingDashboard.css";

export default function MeetingDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const data = user?.role === 'doctor'
        ? await fetchDoctorAppointments()
        : await fetchPatientAppointments();
      setMeetings(data);
    } catch (err) {
      console.error("Failed to load meetings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadMeetings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [date, setDate] = useState("");

  const filtered = useMemo(() => {
    return meetings.filter((m) => {
      const peerName = user?.role === 'doctor'
        ? `${m.patient_first_name || ''} ${m.patient_last_name || ''}`
        : `${m.doctor_first_name || ''} ${m.doctor_last_name || ''}`;
      const text = `${peerName} ${m.notes || ""} ${m.reason || ""}`.toLowerCase();
      if (q && !text.includes(q.toLowerCase())) return false;
      const mStatus = (m.status || "scheduled").toLowerCase();
      if (status !== "all" && mStatus !== status) return false;
      if (date) {
        const d = new Date(m.start_time);
        const sel = new Date(date);
        if (
          d.getFullYear() !== sel.getFullYear() ||
          d.getMonth() !== sel.getMonth() ||
          d.getDate() !== sel.getDate()
        ) return false;
      }
      return true;
    });
  }, [meetings, q, status, date, user]);

  const upcoming = filtered.filter((m) => new Date(m.start_time) >= new Date());
  const past = filtered.filter((m) => new Date(m.start_time) < new Date());

  // Actions
  const handleCreateMeeting = () => {
    if (user?.role === 'doctor') navigate("/doctor/create-meeting");
    else navigate("/appointments/request");
  };
  const handleJoinMeeting = (m) => navigate("/JoinMeeting", { state: { meeting: m } });
  const handleOpenForm = (id) => navigate(`/fiche/${id}`);
  const handleCancelMeeting = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this meeting?")) return;
    try {
      await cancelAppointment(id);
      loadMeetings();
    } catch (err) {
      alert("Failed to cancel meeting");
    }
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const StatusBadge = ({ s }) => (
    <span className={`md-badge md-badge--${s}`}>
      {s.replace("-", " ")}
    </span>
  );

  return (
    <div className="md-container">
      {/* Header */}
      <div className="md-header">
        <div>
          <h1 className="md-title">{t("meeting.dashboard_title")}</h1>
          <p className="md-subtitle">{t("meeting.dashboard_subtitle")}</p>
        </div>
        <button className="md-btn md-btn--primary" onClick={handleCreateMeeting}>
          {t("meeting.create_btn")}
        </button>
      </div>

      {loading && <div style={{ padding: 20 }}>Loading meetings...</div>}

      {/* Filters */}
      <div className="md-filters">
        <input
          className="md-input"
          placeholder={t("meeting.search_placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="md-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="all">{t("meeting.all_statuses")}</option>
          <option value="scheduled">{t("meeting.status_scheduled") || "Scheduled"}</option>
          <option value="in-progress">{t("meeting.status_in_progress") || "In progress"}</option>
          <option value="completed">{t("meeting.status_completed") || "Completed"}</option>
          <option value="canceled">{t("meeting.status_canceled") || "Canceled"}</option>
        </select>
        <input
          type="date"
          className="md-select"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      {/* Upcoming */}
      <section className="md-section">
        <div className="md-section-head">
          <h2 className="md-h2">{t("meeting.upcoming")}</h2>
          <span className="md-count">{upcoming.length} {t("meeting.count_suffix") || "meeting(s)"}</span>
        </div>

        <ul className="md-grid">
          {upcoming.length === 0 && (
            <li className="md-empty">{t("meeting.no_upcoming")}</li>
          )}

          {upcoming.map((m) => (
            <li key={m.id} className="md-card">
              <div className="md-card-top">
                <div>
                  <div className="md-name">
                    {user?.role === 'doctor'
                      ? `${m.patient_first_name || ''} ${m.patient_last_name || ''}`
                      : `Dr. ${m.doctor_first_name || ''} ${m.doctor_last_name || ''}`}
                  </div>
                  <div className="md-row">
                    <StatusBadge s={m.status || "scheduled"} />
                    <span className="md-dot" />
                    <span className="md-muted">{m.mode === "video" ? t("meeting.video") : t("meeting.in_person")}</span>
                  </div>
                </div>
                <div className="md-when">{fmt(m.start_time)}</div>
              </div>

              <div className="md-note">{m.notes}</div>

              <div className="md-actions">
                {m.mode === "video" && (m.status || "scheduled").toLowerCase() !== "canceled" && (
                  <button className="md-btn md-btn--ghost" onClick={() => handleJoinMeeting(m)}>
                    {t("meeting.join")}
                  </button>
                )}
                <button className="md-btn md-btn--ghost" onClick={() => handleOpenForm(m.id)}>
                  {t("meeting.fiche")}
                </button>
                {(m.status || "scheduled").toLowerCase() === "scheduled" && (
                  <button className="md-btn md-btn--danger" onClick={() => handleCancelMeeting(m.id)}>
                    {t("meeting.cancel")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Past */}
      <section className="md-section">
        <div className="md-section-head">
          <h2 className="md-h2">{t("meeting.past")}</h2>
          <span className="md-count">{past.length} {t("meeting.count_suffix") || "meeting(s)"}</span>
        </div>

        <ul className="md-grid">
          {past.length === 0 && <li className="md-empty">{t("meeting.no_past")}</li>}

          {past.map((m) => (
            <li key={m.id} className="md-card">
              <div className="md-card-top">
                <div>
                  <div className="md-name">
                    {user?.role === 'doctor'
                      ? `${m.patient_first_name || ''} ${m.patient_last_name || ''}`
                      : `Dr. ${m.doctor_first_name || ''} ${m.doctor_last_name || ''}`}
                  </div>
                  <div className="md-row">
                    <StatusBadge s={m.status || "completed"} />
                    <span className="md-dot" />
                    <span className="md-muted">{m.mode === "video" ? t("meeting.video") : t("meeting.in_person")}</span>
                  </div>
                </div>
                <div className="md-when">{fmt(m.start_time)}</div>
              </div>

              <div className="md-note">{m.notes}</div>

              <div className="md-actions">
                <button className="md-btn md-btn--ghost" onClick={() => handleOpenForm(m.id)}>
                  Fiche Patient
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
